import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { auth, db } from '../../store/api/fbConfig';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  doc,
  query,
  where,
  or,
  and,
  getDocs,
  getDoc,
  orderBy as orderByFn,
} from 'firebase/firestore';
import * as timeago from 'timeago.js';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Toolbar,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Loader from '../../components/other/Loader';
import GroupSideBar from '../../components/group/GroupSideBar';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Cookies from 'js-cookie';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  sidebar: {
    width: 240,
    flexShrink: 0,
    background: '#f5f5f5',
    padding: '8px',
  },
  content: {
    flexGrow: 1,
    maxWidth: '100%',
    padding: '16px',
  },
  mobileSidebar: {
    width: '100%',
    background: '#f5f5f5',
    padding: '8px',
    marginBottom: '16px',
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  toggleBtn: {
    marginRight: 12,
  },
  Space: {
    flexGrow: 1,
  },
  NameCol: {
    paddingLeft: '20px',
  },
  searchWrap: {
    marginBottom: 16,
    padding: 12,
  },
}));

const headCells = [
  { id: 'name', label: 'Name' },
  { id: 'DeviceId', label: 'Device Id' },
  { id: 'dateLastEntries', label: 'Last Entry' },
  { id: 'dateCreated', label: 'Date Added' },
  { id: 'entries', label: 'Number of entries' },
];

function getDeviceSearchText(device = {}) {
  const users =
    device.users && typeof device.users === 'object'
      ? Object.keys(device.users).join(' ')
      : '';

  const groups = Array.isArray(device.groups) ? device.groups.join(' ') : '';

  return [
    device.id,
    device.name,
    device.DeviceId,
    device.deviceId,
    device.owner,
    users,
    groups,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function formatEntryCount(value) {
  const val = Number(value || 0);

  if (val > 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val > 1_000) return `${(val / 1_000).toFixed(1)}K`;

  return val;
}

const DevicesView = () => {
  const { id: groupId } = useParams();
  const classes = useStyles();

  const isSmallScreen = useMediaQuery('(max-width:768px)');

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const cookieVal = Cookies.get('sidebarOpen');
    return cookieVal === undefined ? !isSmallScreen : cookieVal === 'true';
  });

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [group, setGroup] = useState(null);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('desc');
  const [searchText, setSearchText] = useState('');

  const filteredDevices = useMemo(() => {
    const term = searchText.trim().toLowerCase();

    if (!term) return devices;

    const words = term.split(/\s+/).filter(Boolean);

    return devices.filter((device) => {
      const haystack = getDeviceSearchText(device);
      return words.every((word) => haystack.includes(word));
    });
  }, [devices, searchText]);

  const toggleSidebar = () => {
    const newVal = !sidebarOpen;
    setSidebarOpen(newVal);
    Cookies.set('sidebarOpen', newVal, { expires: 365 });
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return () => unsub();
  }, []);

  useEffect(() => {
    const loadDevices = async () => {
      if (!user) {
        setDevices([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const deviceRef = collection(db, 'Devices');

        let deviceQuery;

        if (groupId) {
          deviceQuery = query(
            deviceRef,
            and(
              or(
                where('owner', '==', user.uid),
                where(`users.${user.uid}`, 'in', ['viewer', 'editor', 'admin'])
              ),
              where('groups', 'array-contains', groupId)
            ),
            orderByFn(orderBy, order)
          );
        } else {
          deviceQuery = query(
            deviceRef,
            or(
              where('owner', '==', user.uid),
              where(`users.${user.uid}`, 'in', ['viewer', 'editor', 'admin'])
            ),
            orderByFn(orderBy, order)
          );
        }

        const snap = await getDocs(deviceQuery);
        const list = snap.docs.map((deviceDoc) => ({
          id: deviceDoc.id,
          ...deviceDoc.data(),
        }));

        setDevices(list);
      } catch (error) {
        console.error('Failed to load devices:', error);
        setDevices([]);
      } finally {
        setLoading(false);
      }
    };

    loadDevices();
  }, [user, groupId, order, orderBy]);

  useEffect(() => {
    const loadGroupDetails = async () => {
      if (!groupId) {
        setGroup(null);
        return;
      }

      const groupDoc = await getDoc(doc(db, 'Groups', groupId));

      if (groupDoc.exists()) {
        setGroup({ id: groupId, ...groupDoc.data() });
      } else {
        setGroup(null);
      }
    };

    loadGroupDetails();
  }, [groupId]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <main className={classes.root}>
      {!isSmallScreen && sidebarOpen && (
        <div className={classes.sidebar}>
          <GroupSideBar />
        </div>
      )}

      <div className={classes.content}>
        <Toolbar>
          <IconButton onClick={toggleSidebar} className={classes.toggleBtn} size="large">
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <h2 style={{ marginRight: 16 }}>
            {group ? `Group: ${group.name}` : 'All Devices'}
          </h2>

          <div className={classes.Space} />

          <Button component={NavLink} to="/devices/add" variant="contained">
            Add Device
          </Button>
        </Toolbar>

        {isSmallScreen && sidebarOpen && (
          <div className={classes.mobileSidebar}>
            <GroupSideBar />
          </div>
        )}

        <Paper className={classes.searchWrap} elevation={0} variant="outlined">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search devices by name, ID, group, owner, or user"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchText('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', opacity: 0.75 }}>
              {filteredDevices.length} of {devices.length}
            </Typography>
          </Stack>
        </Paper>

        {loading ? (
          <Loader />
        ) : (
          <TableContainer>
            <Table className={classes.table} size="medium">
              <TableHead>
                <TableRow>
                  {headCells.map((cell) => (
                    <TableCell key={cell.id} sortDirection={orderBy === cell.id ? order : false}>
                      <TableSortLabel
                        active={orderBy === cell.id}
                        direction={orderBy === cell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(cell.id)}
                      >
                        {cell.label}

                        {orderBy === cell.id && (
                          <span className={classes.visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </span>
                        )}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredDevices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length}>
                      <Typography variant="body2" sx={{ py: 3, textAlign: 'center', opacity: 0.75 }}>
                        {searchText ? 'No devices match your search.' : 'No devices found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDevices.map((device) => {
                    const created = device.dateCreated?.seconds
                      ? timeago.format(device.dateCreated.seconds * 1000)
                      : '';

                    const lastEntry = device.dateLastEntries?.seconds
                      ? timeago.format(device.dateLastEntries.seconds * 1000)
                      : '';

                    const entryCount = formatEntryCount(device.entries);
                    const displayDeviceId = device.DeviceId || device.deviceId || device.id;

                    return (
                      <TableRow key={device.id} hover>
                        <TableCell className={classes.NameCol}>
                          <NavLink to={`/devices/view/${device.id}`}>
                            {device.name || 'Unnamed device'}
                          </NavLink>
                        </TableCell>

                        <TableCell>{displayDeviceId}</TableCell>
                        <TableCell>{lastEntry}</TableCell>
                        <TableCell>{created}</TableCell>
                        <TableCell>{entryCount}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </main>
  );
};

export default DevicesView;